import { useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

/**
 * api.ts — lo que pide el armazón. Módulo 18, capa B.
 *
 * Aquí sí se usa `lib/api`: el cromo solo existe cuando hay sesión, así que el
 * 401 centralizado que devuelve al login es exactamente lo que se quiere. Es lo
 * contrario del formulario público y del acceso, que se usan sin cuenta.
 */

/* ── Búsqueda global ───────────────────────────────────────────────────── */

export interface ResultadoDeBusqueda {
  title: string;
  subtitle?: string | null;
  route: string;
  avatar_url?: string | null;
}

export interface GrupoDeResultados {
  label: string;
  icon: string;
  items: ResultadoDeBusqueda[];
}

export interface Busqueda {
  query: string;
  groups: GrupoDeResultados[];
  total: number;
}

/**
 * Busca personas, cursos, eventos y publicaciones.
 *
 * El servidor ya filtra por los módulos que alcanza quien pregunta, así que no
 * hay nada que ocultar aquí: lo que vuelve es lo que esa persona puede ver.
 */
export function useBusquedaGlobal(texto: string) {
  const q = texto.trim();
  return useQuery({
    queryKey: ['armazon', 'busqueda', q] as const,
    queryFn: () => api.get<Busqueda>('/search/', { q }),
    // Menos de dos letras no se pregunta: el servidor devuelve vacío igual y
    // sería una petición por tecla.
    enabled: q.length >= 2,
    // La lista anterior se queda mientras llega la nueva, para que no
    // parpadee entre letra y letra.
    placeholderData: (previa) => previa,
    staleTime: 30_000,
  });
}

/* ── Quién mira ────────────────────────────────────────────────────────── */

/**
 * La foto de perfil de quien tiene la sesión abierta.
 *
 * Se pide y no se lee de la sesión porque **no está ahí**: `Auth.saveSession`
 * guarda id, rol, nombre y tres banderas, y nada más. El armazón lo resolvía con
 * un `API.get('/auth/me')` suelto en `startApp()` por esta misma razón; con
 * `Auth.getUser()?.avatar_url` el avatar habría salido siempre con las
 * iniciales, para todo el mundo, sin que nada fallara.
 *
 * La URL es prefirmada y caduca (`MINIO_PRESIGN_EXPIRY`). El armazón lo tapaba
 * con un `onerror` en el `<img>` que caía a las iniciales; aquí se vuelve a pedir
 * al recuperar el foco, así que la foto vuelve en vez de quedarse rota hasta la
 * siguiente recarga.
 */
export function useAvatarPropio() {
  return useQuery({
    queryKey: ['armazon', 'avatar-propio'] as const,
    queryFn: () => api.get<{ avatar_url?: string | null }>('/auth/me'),
    staleTime: 10 * 60_000,
    refetchOnWindowFocus: true,
  });
}

/* ── Notificaciones ────────────────────────────────────────────────────── */

export interface Notificacion {
  notification_id: number;
  is_read: boolean;
  title: string;
  message: string;
  created_at: string;
}

/**
 * Cuántas sin leer. Un `COUNT`, no una página.
 *
 * Hasta el 2026-09-28 esto pedía veinte notificaciones y contaba las no leídas
 * de esas veinte: con veinticinco sin leer decía 20 y no se movía al leer
 * cinco. Ver `docs/Cambios/2026-09-28-la-campana-que-se-quedaba-en-20.md`.
 */
export function useSinLeer(habilitado = true) {
  return useQuery({
    queryKey: ['armazon', 'sin-leer'] as const,
    queryFn: () => api.get<{ unread: number }>('/notifications/unread-count'),
    enabled: habilitado,
    // El mismo minuto que tenía el `setInterval` del armazón.
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
}

export function useNotificaciones(habilitado: boolean) {
  return useQuery({
    queryKey: ['armazon', 'notificaciones'] as const,
    queryFn: () => api.get<Notificacion[]>('/notifications/', { limit: 10 }),
    enabled: habilitado,
  });
}

/**
 * Marca como leídas las que se acaban de enseñar.
 *
 * Una petición por notificación, que es lo que el servidor ofrece hoy. Los
 * fallos se cuentan pero **no se propagan**: una que no se pueda marcar vuelve a
 * salir la próxima vez, que es la degradación correcta. Lo que sí hace falta es
 * saberlo, porque «me lo marca como leído y reaparece» es imposible de
 * diagnosticar sin ese rastro.
 */
export function useMarcarLeidas() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ids: number[]) => {
      const resultados = await Promise.allSettled(
        ids.map((id) => api.post<{ message: string }>(`/notifications/${id}/read`, null)),
      );
      const fallaron = resultados.filter((r) => r.status === 'rejected').length;
      if (fallaron) {
        console.warn(`[armazón] ${fallaron} de ${ids.length} no se pudieron marcar como leídas`);
      }
      return { fallaron };
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['armazon', 'sin-leer'] });
      void qc.invalidateQueries({ queryKey: ['armazon', 'notificaciones'] });
    },
  });
}

/**
 * El aviso del NAVEGADOR cuando llega algo nuevo, y el permiso para darlo.
 *
 * Esto no es la campana: es la notificación del sistema operativo, la que se ve
 * con el portal en otra pestaña. Vivía dentro de `loadNotifBadge()` y es la parte
 * que se habría perdido sin hacer ruido al pasar el contador a React — el número
 * de la campana seguiría subiendo y nadie echaría en falta lo otro hasta
 * preguntarse por qué ya no avisa.
 *
 * Tres cosas se conservan tal cual:
 *
 * - **El título se pide solo cuando hay algo nuevo y el permiso está dado.** Es
 *   una petición más, y hacerla en cada latido sería pedir la lista cada minuto
 *   por persona conectada para no usarla.
 * - **El permiso se pide a los cuatro segundos, una vez.** No al entrar: un
 *   diálogo del navegador encima de la pantalla de carga se rechaza por reflejo,
 *   y el rechazo es definitivo.
 * - **Los dos `try` siguen ahí.** `requestPermission` lanza en Safari antiguo
 *   fuera de un gesto del usuario, y `new Notification` lanza en Android aunque
 *   el permiso esté concedido —ahí solo se permiten desde un service worker—. No
 *   poder avisar por el sistema no es un fallo del portal: la campana de dentro
 *   ya se actualizó.
 */
export function useAvisoNativo(sinLeer: number | undefined) {
  const anterior = useRef<number | null>(null);

  useEffect(() => {
    if (!('Notification' in window) || Notification.permission !== 'default') return undefined;
    const t = window.setTimeout(() => {
      try { void Notification.requestPermission(); } catch { /* Safari antiguo */ }
    }, 4000);
    return () => window.clearTimeout(t);
  }, []);

  useEffect(() => {
    if (sinLeer === undefined) return;
    const previo = anterior.current;
    anterior.current = sinLeer;
    // La primera lectura solo fija el punto de partida: sin esto, entrar con
    // cinco sin leer de ayer dispararía un aviso de escritorio al cargar.
    if (previo === null || sinLeer <= previo) return;
    if (!('Notification' in window) || Notification.permission !== 'granted') return;

    void (async () => {
      let n: Notificacion | undefined;
      try {
        n = ((await api.get<Notificacion[]>('/notifications/', { limit: 1 })) || [])
          .find((x) => !x.is_read);
      } catch {
        // Sin el título no hay aviso del sistema, y no pasa nada: la campana ya
        // lleva el número. Avisar de esto por consola cada minuto sería ruido.
        return;
      }
      try {
        new Notification(n?.title || 'Bienestar Su Servicio', {
          body: n?.message || '',
          icon: '/static/manifest.json',
        });
      } catch { /* Android: solo desde un service worker */ }
    })();
  }, [sinLeer]);
}

/* ── El aviso de datos pendientes en la ficha ──────────────────────────── */

export interface FichaPendiente {
  /** Las etiquetas de lo que falta, tal cual: el servidor manda cadenas. */
  pendientes?: string[];
}

/**
 * Qué le falta por rellenar a quien mira.
 *
 * Vive en el armazón y no dentro de una pantalla porque tiene que verse se
 * navegue a donde se navegue: el filtro de datos iniciales solo dispara en
 * «preregistro», así que una columna que Bienestar agregue después no se le
 * pediría NUNCA a quien ya está activo.
 */
export function useFichaPendiente(habilitado: boolean) {
  return useQuery({
    queryKey: ['armazon', 'ficha-pendiente'] as const,
    queryFn: () => api.get<FichaPendiente>('/auth/mi-ficha'),
    enabled: habilitado,
    staleTime: 5 * 60_000,
  });
}
